/**
 * EncounterList Page
 *
 * Displays list of encounters from external EHR systems in connected mode.
 * Uses the EncounterList component for consistent UI/UX with standalone mode.
 */

import { useState, useCallback } from 'react';
import { useCapabilities } from '@/hooks/useCapabilities';
import { EncounterList as EncounterListComponent, UnifiedEncounterItem, EncounterListConfig } from '@/components/encounters';
import EncounterDetailPanel from '@/components/EncounterDetailPanel';
import { Encounter } from '@/types/encounter';

const EncounterListPage: React.FC = () => {
  const { loading: capabilitiesLoading = true } = useCapabilities();
  const [isDetailPanelOpen, setIsDetailPanelOpen] = useState(false);
  const [selectedEncounter, setSelectedEncounter] = useState<Encounter | null>(null);

  // Handle clicking on an encounter to open detail panel
  const handleItemClick = useCallback((item: UnifiedEncounterItem) => {
    // The raw encounter data is stored in _raw when _type is 'encounter'
    if (item._type === 'encounter') {
      setSelectedEncounter(item._raw as Encounter);
      setIsDetailPanelOpen(true);
    }
  }, []);

  // Handle closing the detail panel
  const handleCloseDetailPanel = useCallback(() => {
    setIsDetailPanelOpen(false);
    setSelectedEncounter(null);
  }, []);

  // Show loading while capabilities are being determined
  if (capabilitiesLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading capabilities...</p>
        </div>
      </div>
    );
  }

  // Configure the EncounterList component for connected mode
  const encounterListConfig: EncounterListConfig = {
    mode: 'connected',
    title: 'Encounters',
    subtitle: 'View and manage encounters from external EHR systems',
    canCreate: false, // Cannot create encounters in connected mode - managed by EHR
    enableSearch: true,
    enableFilters: true,
    enablePagination: true,
    enableInfiniteScroll: true,
    defaultPageSize: 20,
    pageSizeOptions: [10, 20, 50, 100],
    showMatchStatus: true,
    showExternalSystem: true,
    onItemClick: handleItemClick,
    onViewDetails: handleItemClick,
  };

  return (
    <div className="space-y-6">
      <EncounterListComponent config={encounterListConfig} />

      {/* Encounter Detail Panel */}
      <EncounterDetailPanel
        encounter={selectedEncounter}
        isOpen={isDetailPanelOpen}
        onClose={handleCloseDetailPanel}
      />
    </div>
  );
};

export default EncounterListPage;
